import axios, { AxiosInstance, AxiosResponse } from 'axios';

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (typeof window !== 'undefined'
    ? `${window.location.protocol}//${window.location.hostname}:8945/api/v1`
    : 'http://localhost:8945/api/v1');

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`, {
      params: config.params,
      timestamp: new Date().toISOString()
    });
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response: AxiosResponse) => {
    const responseData = response.data?.data;
    const isArray = Array.isArray(responseData);
    console.log(`API Response: ${response.config.url}`, {
      status: response.status,
      dataLength: isArray ? responseData.length : (responseData ? 1 : 0),
      count: response.data?.count || 0
    });
    return response;
  },
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      
      // Only redirect if not already on login page
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Roles API (public, for registration dropdown)
export const rolesAPI = {
  getAll: () => api.get('/roles'),
};

// Auth API
export const authAPI = {
  register: (userData: any) => api.post('/auth/register', userData),
  login: (credentials: any) => api.post('/auth/login', credentials),
  refreshToken: (refreshToken: string) => api.post('/auth/refresh', { refreshToken }),
  logout: () => api.post('/auth/logout'),
  getProfile: () => api.get('/auth/me'),
  validateToken: () => api.post('/auth/validate'),
};

// Users API
export const usersAPI = {
  getAll: () => api.get('/users'),
  getById: (id: number) => api.get(`/users/${id}`),
  search: (params: any) => api.get('/users/search', { params }),
  getNominations: (id: number) => api.get(`/users/${id}/nominations`),
  update: (id: number, data: any) => api.put(`/users/${id}`, data),
  remove: (id: number) => api.delete(`/users/${id}`),
};

// Nominations API
export const nominationsAPI = {
  getAll: () => api.get('/nominations', {
    params: { _t: Date.now() },
    headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
  }),
  getUnapproved: () => api.get('/nominations/unapproved'),
  approve: (nominationIds: number[]) => api.post('/nominations/approve', { nominationIds }),
  getById: (id: number) => api.get(`/nominations/${id}`),
  search: (params: any) => api.get('/nominations/search', { params }),
  getContainers: (id: number) => api.get(`/nominations/${id}/containers`),
  getStats: () => api.get('/nominations/stats/summary'),
  create: (data: any) => api.post('/nominations', data),
  update: (id: number, data: any) => api.put(`/nominations/${id}`, data),
  remove: (id: number) => api.delete(`/nominations/${id}`),
};

// Containers API
export const containersAPI = {
  getAll: () => api.get('/containers', { 
    params: { _t: Date.now() },
    headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
  }),
  getById: (id: number) => api.get(`/containers/${id}`),
  search: (params: any) => api.get('/containers/search', { params }),
  getByNomination: (nominationId: number) => api.get(`/containers/by-nomination/${nominationId}`),
  getWeightStats: () => api.get('/containers/stats/weights'),
  create: (data: any) => api.post('/containers', data),
  update: (id: number, data: any) => api.put(`/containers/${id}`, data),
  bulkUpdate: (items: Array<{ containerId: number; [key: string]: any }>) => api.patch('/containers', items),
  remove: (id: number) => api.delete(`/containers/${id}`),
  getSealPhoto: (id: number) => api.get(`/containers/${id}/photo/seal`, { responseType: 'blob' }),
  getContainerPhotosExtracted: (id: number) => api.get<{ status: string; data: { images: Array<{ base64: string; contentType: string; name: string }> } }>(`/containers/${id}/photo/container/extracted`),
  getCategoryPhotoExtracted: (containerId: number, photoId: number) =>
    api.get<{
      status: string;
      data: {
        category: string;
        images: Array<{ base64: string; contentType: string; name: string }>;
      };
    }>(`/containers/${containerId}/photos/${photoId}/extracted`),
};

// Documents API
export const documentsAPI = {
  downloadNominationReport: (nominationId: number) => api.get(`/documents/nomination/${nominationId}`, {
    responseType: 'blob',
    headers: {
      'Accept': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    }
  }),
};

export interface MobileVersionPayload {
  version: string;
  buildNumber: number;
  mandatory?: boolean;
  changelog?: string[];
  apk: File;
}

export const mobileVersionAPI = {
  getLatest: () => api.get('/version-mobile'),
  create: (payload: MobileVersionPayload) => {
    const formData = new FormData();
    formData.append('apk', payload.apk);
    formData.append('version', payload.version);
    formData.append('buildNumber', String(payload.buildNumber));
    formData.append('mandatory', String(Boolean(payload.mandatory)));

    if (payload.changelog && payload.changelog.length > 0) {
      formData.append('changelog', JSON.stringify(payload.changelog));
    }

    return api.post('/version-mobile', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

export default api;
